<template>
  <div class="mc-video-history-new__filelist-section-wrapper">
    <div class="mc-video-history-new__filelist-timeline-wrapper">
      <div class="mc-video-history-new__filelist-timeline-control-wrapper">
        <TimelineSearch :searchDisabled="loadingFilelist" />
        <div class="mc-video-history-new__filelist-timeline-play-button-wrapper">
          <i
            v-if="isThereIsFilelistPlaying"
            class="mc-icon icon-fill_pause"
            @click="handleClickPauseTimeline"
          ></i>
          <i
            v-else
            class="mc-icon icon-fill_play"
            :class="{ disabled: timelineDisabled }"
            @click="handleClickPlayTimeline"
          ></i>
        </div>
      </div>
      <div
        ref="timelineScroller"
        class="mc-video-history-new__filelist-timeline-scroller"
        :class="{ 'is-dragging': isDraggingMarker }"
        @wheel.prevent="handleTimelineZoom($event, $refs.timelineScroller, $refs.timelineContainer)"
      >
        <div ref="timelineContainer" :style="{ width: `${zoomPercentage}%` }">
          <Timeline
            :isLoading="loadingFilelist"
            :startTime="getUtcDateFromLocalDate(filelistSelected?.startTime, deviceTimezone)"
            :endTime="getUtcDateFromLocalDate(filelistSelected?.endTime, deviceTimezone)"
            :markerPosition="displayedMarkerPosition"
            :countTimeLabel="countTimeLabel"
            :zoomPercentage="zoomPercentage"
            :filelistUnion="
              filelistSelected
                ? [{ startTime: filelistSelected.startTime, endTime: filelistSelected.endTime }]
                : []
            "
            :timelineDisabled="timelineDisabled"
            @clickTimelineDatetime="handleClickTimelineDatetime"
            @markerDragStart="handleMarkerDragStart"
          />
        </div>
      </div>
    </div>
    <TableFilelist
      :mediaFilenameSelected="mediaFilenameSelected"
      :channelPlay="channelPlay"
      @handlePlay="handleTablePlay"
      @handlePause="handleTablePause"
    />
  </div>
</template>
<script>
import Timeline from '../Timeline.vue'
import TableFilelist from './Table.vue'
import { Helper } from '@/mixin/helper'
import { mapState, mapGetters } from 'vuex'
import ZoomTimelineMixin from '../../../mixins/zoom-timeline.js'
import TimelineSearch from '../TimelineSearch.vue'
import moment from 'moment'

moment.locale('id')

export default {
  components: {
    Timeline,
    TableFilelist,
    TimelineSearch,
  },

  mixins: [Helper, ZoomTimelineMixin],

  data() {
    return {
      mediaFilenameSelected: null,
      channelPlay: null,

      markerPositionTimeline: 0,
      draftMarkerPosition: null,
      isDraggingMarker: false,
      markerDragDebounce: null,
      markerDragDebounceMs: 300,
    }
  },

  computed: {
    ...mapState('videoHistory', [
      'mapFilelist',
      'loadingFilelist',
      'mapWebviewStatePerChannel',
      'vehicleStatuses',
      'vehicleIdActive',
      'mapWebviewDatetimePerChannel',
      'mapWebviewBufferedTimePerChannel',
      'activeTab',
      'videoChannelActive',
      'filelistPlaySelected',
    ]),
    ...mapGetters('videoHistory', ['deviceTimezone']),
    bufferedTimeState() {
      return (channel) => this.mapWebviewBufferedTimePerChannel[channel]
    },
    filelistSelected() {
      const filelist = this.mapFilelist[this.channelPlay]
      if (!filelist) return null
      return filelist.find((item) => item.mediaFilename === this.mediaFilenameSelected)
    },
    displayedMarkerPosition() {
      return this.draftMarkerPosition === null
        ? this.markerPositionTimeline
        : this.draftMarkerPosition
    },
    isThereIsFilelistPlaying() {
      const state = this.mapWebviewStatePerChannel[this.channelPlay]
      return (
        !!this.mediaFilenameSelected &&
        (state === 'CONNECTING' || state === 'PLAYING' || state === 'FREEZE' || state === 'LOAD')
      )
    },
    timelineDisabled() {
      return this.loadingFilelist || !this.mediaFilenameSelected
    },
    imei() {
      const imei = this.vehicleStatuses[this.vehicleIdActive]?.vehicle?.imei
      return imei
    },
    filenameSelectedDurationSeconds() {
      if (!this.filelistPlaySelected?.mediaFilename) return 0
      const _start = new Date(this.filelistPlaySelected.startTime).getTime()
      const _end = new Date(this.filelistPlaySelected.endTime).getTime()
      return (_end - _start) / 1000
    },
  },

  watch: {
    mapWebviewDatetimePerChannel: {
      handler() {
        if (!this.mediaFilenameSelected || !this.channelPlay) return
        Object.entries(this.mapWebviewDatetimePerChannel).forEach(([channel, datetime]) => {
          if (Number(channel) !== Number(this.channelPlay)) return
          this.goToMarkerPosition(datetime)
        })
      },
      immediate: true,
      deep: true,
    },
    activeTab(activeTab, oldActiveTab) {
      if (oldActiveTab === 'filelist' && activeTab === 'timeline' && this.mediaFilenameSelected) {
        this.mediaFilenameSelected = null
        this.markerPositionTimeline = 0
      }
    },
  },

  beforeDestroy() {
    window.clearTimeout(this.markerDragDebounce)
    this.removeMarkerDragListeners()
  },

  methods: {
    postToStreamIframeTogglePaused(channel, isPaused) {
      this.$store.dispatch('videoHistory/postToStreamIframe', {
        channel: channel,
        imei: this.imei,
        payload: {
          type: 'VIDEO_HISTORY_TOGGLE_PAUSED',
          data: { isPaused: isPaused, imei: this.imei, channel: channel },
        },
      })
    },

    handleClickPauseTimeline() {
      this.postToStreamIframeTogglePaused(this.channelPlay, true)
    },

    handleClickPlayTimeline() {
      this.postToStreamIframeTogglePaused(this.channelPlay, false)
    },

    async handleTablePlay(mediaFilename, channelPlay, startTime, endTime) {
      const isPaused = this.mapWebviewStatePerChannel[channelPlay] === 'PAUSED'
      if (isPaused && mediaFilename === this.mediaFilenameSelected) {
        this.postToStreamIframeTogglePaused(channelPlay, false)
      } else {
        this.initAndPlayVideoFromFilelist(mediaFilename, channelPlay, startTime, endTime)
      }
    },

    handleTablePause(mediaFilename, channelPlay) {
      this.postToStreamIframeTogglePaused(channelPlay, true)
    },

    async initAndPlayVideoFromFilelist(mediaFilename, channelPlay, startTime, endTime) {
      this.resetMarkerState()

      this.mediaFilenameSelected = mediaFilename
      this.channelPlay = channelPlay

      this.$store.commit('videoHistory/resetVideoChannelActive')

      if (this.filelistPlaySelected?.mediaFilename) {
        this.$store.dispatch(
          'videoHistory/removeChannelToGridWebview',
          this.filelistPlaySelected.channel
        )
      }

      await this.$nextTick()

      this.$store.commit('videoHistory/setFilelistPlaySelected', {
        startTime: startTime,
        endTime: endTime,
        channel: channelPlay,
        mediaFilename: mediaFilename,
      })

      await this.$nextTick()
      this.$store.commit('videoHistory/addVideoChannelActive', channelPlay)
      this.$store.dispatch('videoHistory/playChannel', channelPlay)

      this._zoomToMaxAtPosition()
    },

    resetMarkerState() {
      window.clearTimeout(this.markerDragDebounce)
      this.removeMarkerDragListeners()

      this.isDraggingMarker = false
      this.draftMarkerPosition = null
      this.markerPositionTimeline = 0
    },

    getSelectedPeriodUtc() {
      if (!this.filelistSelected) return null

      const startTime = this.getUtcDateFromLocalDate(
        this.filelistSelected.startTime,
        this.deviceTimezone
      )

      const endTime = this.getUtcDateFromLocalDate(
        this.filelistSelected.endTime,
        this.deviceTimezone
      )

      const start = moment.utc(startTime)
      const end = moment.utc(endTime)

      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
        return null
      }

      return {
        start,
        end,
      }
    },

    goToMarkerPosition(datetime) {
      const period = this.getSelectedPeriodUtc()
      const selected = moment.utc(datetime)

      if (!period || !selected.isValid()) return

      const duration = period.end.diff(period.start)
      const position = ((selected.valueOf() - period.start.valueOf()) / duration) * 100

      this.markerPositionTimeline = Math.min(Math.max(position, 0), 100)
    },

    async handleTimeChanged(datetime) {
      this.goToMarkerPosition(datetime)
      const buffered = this.bufferedTimeState(this.channelPlay)
      if (!buffered) return

      if (this.isBetweenDate(datetime, buffered.start, buffered.end)) {
        this.$store.dispatch('videoHistory/postToStreamIframe', {
          imei: this.imei,
          channel: this.channelPlay,
          payload: {
            type: 'VIDEO_HISTORY_SEEK',
            data: {
              channel: this.channelPlay,
              imei: this.imei,
              dateTime: datetime,
            },
          },
        })
      } else {
        this.$store.commit('videoHistory/setDateTimeIntentToChangeInFilelist', datetime)
        await this.$nextTick()
        this.videoChannelActive.forEach((channel) => {
          this.$store.dispatch('videoHistory/playChannel', channel)
        })
        await this.$nextTick()
        this.$store.commit('videoHistory/setHasIntentToTimeChange', true)
        this.$store.commit('videoHistory/resetMapWebviewBufferedTimePerChannel')
        this._zoomToMaxAtPosition()
      }
    },

    _zoomToMaxAtPosition() {
      this.zoomToMaxAtPosition(
        this.$refs.timelineScroller,
        this.$refs.timelineContainer,
        this.filenameSelectedDurationSeconds,
        this.markerPositionTimeline
      )
    },

    handleClickTimelineDatetime(datetime) {
      if (this.isDraggingMarker) return
      this.handleTimeChanged(datetime)
    },

    handleMarkerDragStart(event) {
      if (this.loadingFilelist || !this.filelistSelected || this.isDraggingMarker) {
        return
      }

      if (!event || !Number.isFinite(event.clientX)) return

      this.isDraggingMarker = true
      this.draftMarkerPosition = this.markerPositionTimeline

      window.addEventListener('pointermove', this.handleMarkerDragMove)
      window.addEventListener('pointerup', this.handleMarkerDragEnd)
      window.addEventListener('pointercancel', this.handleMarkerDragEnd)

      this.updateMarkerPositionFromPointer(event)
    },

    handleMarkerDragMove(event) {
      if (!this.isDraggingMarker) return

      this.updateMarkerPositionFromPointer(event)
    },

    handleMarkerDragEnd() {
      if (!this.isDraggingMarker) return

      this.isDraggingMarker = false
      this.removeMarkerDragListeners()

      // Memproses posisi terakhir setelah pointer dilepas.
      this.scheduleMarkerPositionCommit()
    },

    updateMarkerPositionFromPointer(event) {
      const container = this.$refs.timelineContainer

      if (!container) return

      const rect = container.getBoundingClientRect()

      if (!rect.width) return

      const cursorX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width)

      this.draftMarkerPosition = (cursorX / rect.width) * 100

      this.scheduleMarkerPositionCommit()
    },

    scheduleMarkerPositionCommit() {
      window.clearTimeout(this.markerDragDebounce)

      this.markerDragDebounce = window.setTimeout(() => {
        this.commitDraggedMarkerPosition()
      }, this.markerDragDebounceMs)
    },

    commitDraggedMarkerPosition() {
      if (this.draftMarkerPosition === null) return

      const datetime = this.getDatetimeFromMarkerPosition(this.draftMarkerPosition)

      if (!datetime) {
        this.draftMarkerPosition = null
        return
      }

      this.handleTimeChanged(datetime)
      this.draftMarkerPosition = null
    },

    getDatetimeFromMarkerPosition(position) {
      const period = this.getSelectedPeriodUtc()

      if (!period) return null

      const normalizedPosition = Math.min(Math.max(Number(position), 0), 100)

      const duration = period.end.diff(period.start)

      return period.start
        .clone()
        .add(duration * (normalizedPosition / 100), 'milliseconds')
        .toISOString()
    },

    removeMarkerDragListeners() {
      window.removeEventListener('pointermove', this.handleMarkerDragMove)
      window.removeEventListener('pointerup', this.handleMarkerDragEnd)
      window.removeEventListener('pointercancel', this.handleMarkerDragEnd)
    },
  },
}
</script>
